import { AssignmentLateOutlined, AssignmentTurnedInOutlined, ChevronRight } from '@mui/icons-material';
import { Badge, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import List from '@mui/material/List';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { track, useContent, useUserCancelTask } from '../../logic';
import { PortalNotificationReference } from '../../types';
import { checkIfPastDue, formatDate, getNotificationsInfo, NotificationsInfo } from '../../utils';
import { MessagingPage, MessageThreadPage, NotificationPage } from '../pages';
import { TrackerCircle } from './ClosingTrackerSection';
import { LinkWithSearchParams } from './LinkWithSearchParams';
import { SectionButtonLink } from './Section';
import CloseIcon from '@mui/icons-material/Close';
import { SectionConfig } from './ClosingPageSectionConfig';
import { NotificationContentOverrideProvider } from '../context/notification-content-override/NotificationContentOverrideContext';
import { useGetOrder } from '../../logic/useApiQuery';
import { useOptimisticUpdateNotificationAndOrderNotification } from '../../logic/optimisticUpdates';
import dayjs from 'dayjs';
import { showUserCancelFunction } from '../../utils/showUserCancelFunction';
import { useAppTheme } from '../../logic/useAppTheme';
import {useOrganization} from '../OrganizationProvider'
import { useIsFeatureEnabled } from '../../logic/useFeatureFlags'
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder';

export function NotificationsSection({ notificationsInfo, sectionConfig }: {
  notificationsInfo: NotificationsInfo;
  sectionConfig: SectionConfig;
}) {
  const { section, id, signingHash, closingId } = useParams()
  const { search } = useLocation()
  const isNotificationSection = section === 'notifications'
  const countText = notificationsInfo.messagesCountText
  const org = useOrganization()
  const twoWayMessagingFlagEnabled = useIsFeatureEnabled('twowaymessaging')
  const twoWayMessagingOrgEnabled = org.TwoWayMessagingEnabled === true
  const {data: orderData} = useGetOrder(closingId ?? '', search ?? '', {
    enabled: twoWayMessagingFlagEnabled && twoWayMessagingOrgEnabled && !!closingId,
  })
  const twoWayMessagingOrderEnabled = !!(orderData?.Order && !isAdhocPortalOrder(orderData.Order) && orderData.Order.TwoWayMessaging === true)
  const twoWayMessaging = twoWayMessagingFlagEnabled && twoWayMessagingOrgEnabled && twoWayMessagingOrderEnabled

  // Hide the notification section if we're on the closing page.
  if (sectionConfig.hideInClosingPage && !isNotificationSection) {
    return <></>
  }

  if (isNotificationSection && (id !== undefined || signingHash !== undefined)) {
    // When TwoWayMessaging is on and this is a Message-type notification, show the thread view.
    const notificationRef = notificationsInfo.notifications.find(n => n.NotificationId === id)
    if (twoWayMessaging && notificationRef?.Type === 'Message') {
      return <MessageThreadPage key={id} />
    }
    // Passing the key here causes the whole NotificationPage component to rerender
    // when the notification id changes.
    return <NotificationContentOverrideProvider><NotificationPage key={id}/></NotificationContentOverrideProvider>
  }

  return isNotificationSection ?
    <>
      { notificationsInfo.hasTasks &&
        <Box sx={{ mb: notificationsInfo.hasMessages ? 3 : 0 }}>
          <TaskList notificationsInfo={notificationsInfo}/>
        </Box>
      }
      { notificationsInfo.hasMessages && (
        twoWayMessaging
          ? <MessagingPage />
          : <NotificationList notificationsInfo={notificationsInfo}/>
      )}
      { (!notificationsInfo.hasMessages && !notificationsInfo.hasTasks) &&
        <Box sx={{ m: 1 }}>No messages or tasks found</Box>
      }
    </>
    :
    <SectionButtonLink
      title={sectionConfig.name}
      secondaryTitle={countText}
      icon={sectionConfig.icon}
      to="notifications"
      searchParams={{component: 'messages'}}
    />
}

//CashToClose tasks have due dates due to the order data and need to be prioritized.
function sortWithCashToCloseFirst(list: PortalNotificationReference[]) {
  return [...list].sort((a, b) => {
    if (a.Type === 'CashToCloseTask' && b.Type !== 'CashToCloseTask') return -1
    if (a.Type !== 'CashToCloseTask' && b.Type === 'CashToCloseTask') return 1
    return 0
  })
}

function TaskList({ notificationsInfo }: {
  notificationsInfo: NotificationsInfo;
}) {
  const uiContent = useContent()
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const globalNotificationsInfo = globalOrderData ? getNotificationsInfo(globalOrderData.Notifications, uiContent, globalOrderData) : notificationsInfo

  const incomplete = sortWithCashToCloseFirst(globalNotificationsInfo.incompleteTasks)
  const complete = globalNotificationsInfo.completeTasks

  return (
    <Box sx={{ width: '100%' }}>
      {incomplete.length > 0 &&
        <TaskSection
          title="Pending Tasks"
          icon={<AssignmentLateOutlined sx={{ color: 'primary.main' }}/>}
          badgeCount={incomplete.length}
          tasks={incomplete}
          sx={{ mb: complete.length > 0 ? 3 : 0 }}
        />
      }
      {complete.length > 0 &&
        <TaskSection
          title="Completed Tasks"
          icon={<AssignmentTurnedInOutlined sx={{ color: 'primary.main' }}/>}
          tasks={complete}
        />
      }
    </Box>
  )
}

function TaskSection({ title, icon, badgeCount, tasks, sx }: {
  title: string;
  icon: JSX.Element;
  badgeCount?: number;
  tasks: PortalNotificationReference[];
  sx?: object;
}) {
  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', overflow: 'hidden', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #ddd' }}>
        {badgeCount !== undefined ?
          <Badge badgeContent={badgeCount} color="primary">
            {icon}
          </Badge> :
          icon
        }
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      <List disablePadding>
        {tasks.map((notification, index) => (
          <Fragment key={notification.NotificationId}>
            <TaskLink notification={notification} isLast={index === tasks.length - 1} />
          </Fragment>
        ))}
      </List>
    </Box>
  )
}


function NotificationList({ notificationsInfo }: {
  notificationsInfo: NotificationsInfo;
}) {
  const notRead = notificationsInfo.unreadMessages
  const read = notificationsInfo.readMessages

  let [searchParams] = useSearchParams();
  const component = searchParams.get('component')
  const scrollToMessages = component === 'messages'

  const messagesRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if(messagesRef?.current && scrollToMessages) {
      messagesRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [scrollToMessages, messagesRef])

  return (
    <>
      <Typography component="span" ref={messagesRef} variant="h5"
        sx={{ fontWeight: '500', lineHeight: '22px', m: 1, scrollMarginTop: {xs: '80px', sm: '10px'}, }}
      >
        Messages
      </Typography>
      <Divider />
      <List sx={{ maxWidth: '800px', 'MuiList-root': { mt: 0 } }}>
        {notRead.length ?
          <Typography sx={{ lineHeight: '22px', color: 'primary.main', m: 1, ml: 2, mt: 0 }}>New!</Typography> :
          ''
        }

        {notRead.map((notification) => (
          <Fragment key={notification.NotificationId}>
            <NotificationLink notification={notification} />
          </Fragment>
        ))}

        {(notRead.length && read.length) ? <Divider sx={{ mt: 3, mb: 3 }}/> : ''}

        {read.map((notification) => (
          <Fragment key={notification.NotificationId}>
            <NotificationLink notification={notification} />
          </Fragment>
        ))}
      </List>
    </>
  )
}

function NotificationLink({ notification }: {
  notification: PortalNotificationReference;
}) {
  const date = formatDate({dateLike: notification.CreatedDateTime}) || ''
  const isNew = !notification.ReadReceipt

  return (
    <ListItemButton
      component={LinkWithSearchParams}
      to={notification.NotificationId}
      sx={{
        width: '100%', height: '69px', boxShadow: 'none',
        display: 'flex', justifyContent: 'start', pl: '15px', pr: '29px',
        fontSize: '18px', fontWeight: '400', backgroundColor: 'white',
        '&:hover': { color: 'initial' },
      }}
    >
      <ListItemText
        primary={notification.NotificationTitle}
        primaryTypographyProps={{ fontWeight: isNew ? 700 : 400, noWrap: true }}
        secondary={date}
        secondaryTypographyProps={{ variant: 'teeny', letterSpacing: '0.1px', color: '#ABABAF', }}
      />
      <ListItemIcon sx={{ ml: 2, minWidth: 0, color: 'primary.main' }}>
        <ChevronRight/>
      </ListItemIcon>
    </ListItemButton>

  )
}

function TaskLink({ notification, isLast }: {
  notification: PortalNotificationReference;
  isLast: boolean;
  updateCompleted?: Function;
}) {
  const { closingId } = useParams()
  const { search } = useLocation()
  const theme = useAppTheme() 
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'))
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const orderKey = globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) ? globalOrderData?.Order?.CompanyIdOrderIdHash || '' : ''  //Have to add this here because useUserCancelTask needs orderKey, it can be an empty string because it returns null if it's an adhoc order.
  const [userCancelOpen, setUserCancelOpen] = useState(false)
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { mutate: mutateUserCancelTask, error: userCancelError, isLoading: userCancelTaskLoading } = useUserCancelTask(orderKey, notification.NotificationId, search, {
    onSuccess: () => {
      optimisticUpdateNotificationAndOrderNotification({orderKey, notificationId: notification.NotificationId, search, patch: { IsCancelled: true, IsComplete: true } })
    },
  })
  const org = useOrganization()

  if (!globalOrderData) { return null }
  if (isAdhocPortalOrder(globalOrderData.Order)) { return null }

  const date = isSmall ? dayjs(notification.CreatedDateTime).format('MM-DD-YY hh:mm A') : formatDate({ dateLike: notification.CreatedDateTime })
  const cashToCloseDueDateObj = dayjs(globalOrderData?.Order?.CashToCloseDetails?.dueDate);
  const cashToCloseDueDate = isLarge ? cashToCloseDueDateObj.format('MM-DD-YY hh:mm A z') : cashToCloseDueDateObj.format('dddd, MMMM D, YYYY [at] hh:mm A z')
  const showDueDate = notification.Type === 'CashToCloseTask' && !notification.IsComplete && !!globalOrderData?.Order?.CashToCloseDetails?.dueDate
  const pastDue = !notification?.IsComplete && notification.Type === 'CashToCloseTask' && !!globalOrderData?.Order?.CashToCloseDetails?.dueDate && checkIfPastDue(globalOrderData?.Order.CashToCloseDetails.dueDate)
  // NOTE: kinda yucky to handle this on the individual tasks but it was also yucky
  // trying to handle it all from the TaskList component (perhaps where it should be)
  function onUserCancelTask() {
    mutateUserCancelTask(undefined)
    setUserCancelOpen(false)
  }

  if (userCancelError) {
    track('error', 'useUserCancelTask', {}, {
      message: JSON.stringify(userCancelError),
      component: 'TaskLink',
    })
  }


  function getTaskStatus() {
    let taskStatus = 'REQUESTED'
    if (notification.IsCancelled) {
      taskStatus = 'CANCELED'
    } else if (notification.IsComplete) {
      taskStatus = 'COMPLETED'
    } else if (pastDue) {
      taskStatus = 'PAST DUE'
    }

    return `TASK ${taskStatus}`
  }

  const showUserCancelButton = showUserCancelFunction(notification, org)

  return (
    <>
      <ListItem
        key={`${notification.NotificationId}-${notification.NotificationTitle}`}
        sx={{
          width: '100%', height: '72px', boxShadow: 'none',
          fontSize: '18px', fontWeight: '400',
        }}
        disablePadding
        secondaryAction={
          showUserCancelButton ?
            <IconButton
              size='small'
              aria-label="cancel"
              disabled={userCancelTaskLoading}
              onClick={() => {
                setUserCancelOpen(true)
              }}
            >
              { userCancelTaskLoading ? <CircularProgress size={20} /> : <CloseIcon fontSize='small'/> }
            </IconButton> : ''
        }
      >
        <ListItemButton
          divider={!isLast}
          sx={{
            backgroundColor: 'white',
            width: '100%', height: '100%',
            display: 'flex', justifyContent: 'space-between', pl: '15px', pr: '29px',
            '&:hover': { color: 'initial', backgroundColor: '#ddd' },
          }}
          component={LinkWithSearchParams}
          to={notification.NotificationId}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <ListItemIcon sx={{ mr: 2, minWidth: 0, color: 'primary.main' }}>
              <TrackerCircle completed={notification.IsComplete || pastDue} connector={false} warning={notification.Type === 'CashToCloseTask' && !notification.IsComplete && !pastDue} cancelled={notification.IsCancelled || pastDue}/>
            </ListItemIcon>
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography
                variant='teeny'
                sx={{
                  letterSpacing: '0.1px', color: '#ABABAF',
                  fontWeight: 600,
                }}
              >
                {getTaskStatus()}
              </Typography>
              <Typography noWrap={true} sx={{ lineHeight: 1.2, pt: '2px', pb: '2px', fontWeight: (notification.IsComplete || pastDue) ? 400 : 700 }}>{notification.NotificationTitle}</Typography>
              { !showDueDate && <Typography
                variant='teeny'
                sx={{
                  letterSpacing: '0.1px', color: '#ABABAF', fontWeight: 600,
                }}
              >Created: {date}</Typography> }
              { showDueDate && cashToCloseDueDate && (
                <Typography
                  variant='teeny'
                  sx={{
                    fontSize: pastDue ? '10px' : '12px',
                    letterSpacing: '0.1px', color: pastDue ? '#ABABAF' : 'black',
                    fontWeight: 600,
                  }}
                >
                  Due: { cashToCloseDueDate }
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <ListItemIcon
              sx={{
                ml: 2, minWidth: 0, color: 'primary.main',
                mr: notification.IsComplete ? 0 : 2,
              }}
            >
              <ChevronRight/>
            </ListItemIcon>
            { showUserCancelButton ? <Divider orientation='vertical' flexItem={true} sx={{ mr: 1, mt: -1, mb: -1 }} /> : ''}
          </Box>
        </ListItemButton>
      </ListItem>


      <Dialog
        key="userCancel"
        open={userCancelOpen}
      >
        <DialogTitle>
          Cancel Task
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Are you sure you want to cancel your "{notification.NotificationTitle}" task?
          </DialogContentText>
          <DialogContentText>
            After canceling, the task will not be available to complete for your transaction.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => setUserCancelOpen(false)}>No, keep it</Button>
          <Button variant="rounded" onClick={onUserCancelTask}>Yes, cancel it</Button>
        </DialogActions>
      </Dialog>
    </>

  )
}
